import { useState } from "react";
import emailjs from "@emailjs/browser";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Globe, Send, Loader2 } from "lucide-react";
import { EMAILJS_CONFIG } from "../../lib/emailjs-config";

const initialForm = {
  nombre: "",
  email: "",
  telefono: "",
  empresa: "",
  asunto: "",
  mensaje: "",
};

export const Contacto = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.mensaje) {
      toast.error("Por favor completa los campos requeridos.");
      return;
    }
    setLoading(true);
    try {
      await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        {
          from_name: form.nombre,
          from_email: form.email,
          phone: form.telefono,
          company: form.empresa,
          subject: form.asunto,
          message: form.mensaje,
          to_email: "contacto@nexoraproductos.com",
          reply_to: form.email,
        },
        { publicKey: EMAILJS_CONFIG.publicKey }
      );
      toast.success("Mensaje enviado. Nuestro equipo te contactará pronto.");
      setForm(initialForm);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo enviar el mensaje. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contacto"
      data-testid="contacto-section"
      className="relative py-20 md:py-28 bg-white"
    >
      <div className="absolute inset-0 tech-grid-bg" />
      <div className="relative max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
          <div className="lg:col-span-7">
            <div className="font-mono-tech text-[11px] tracking-[0.32em] uppercase text-[#843C0C] mb-4 flex items-center gap-2">
              <span className="h-px w-8 bg-[#843C0C]" />
              Contacto
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#203864] tracking-tight leading-[1.05]">
              Conecta con Nexora.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4">
            <p className="text-[#3B3838]/80 text-base md:text-lg leading-relaxed">
              Cuéntanos qué necesitas importar, distribuir o instalar y nuestro equipo
              comercial te responderá con una propuesta técnica y logística.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-[#3B3838]/15">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-white p-7 md:p-10"
            data-testid="contact-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Nombre*" name="nombre" value={form.nombre} onChange={onChange} testId="input-nombre" />
              <Field label="Email*" name="email" type="email" value={form.email} onChange={onChange} testId="input-email" />
              <Field label="Teléfono" name="telefono" type="tel" value={form.telefono} onChange={onChange} testId="input-telefono" />
              <Field label="Empresa" name="empresa" value={form.empresa} onChange={onChange} testId="input-empresa" />
              <div className="md:col-span-2">
                <Field label="Asunto" name="asunto" value={form.asunto} onChange={onChange} testId="input-asunto" />
              </div>
              <div className="md:col-span-2">
                <label className="block">
                  <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3B3838]/70">
                    Mensaje*
                  </span>
                  <textarea
                    name="mensaje"
                    rows={5}
                    value={form.mensaje}
                    onChange={onChange}
                    data-testid="input-mensaje"
                    className="mt-2 w-full bg-[#f4f5f7] border border-[#3B3838]/20 px-4 py-3 text-[#3B3838] focus:outline-none focus:border-[#203864] focus:ring-1 focus:ring-[#203864] transition-all resize-none"
                    placeholder="Cuéntanos sobre tu proyecto o requerimiento..."
                  />
                </label>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-button"
              className="mt-7 w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#203864] hover:bg-[#843C0C] disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 font-medium tracking-wide transition-colors"
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  Enviar mensaje
                  <Send size={16} />
                </>
              )}
            </button>
          </form>

          {/* Contact info */}
          <aside
            className="lg:col-span-5 bg-[#3B3838] text-white p-7 md:p-10 relative overflow-hidden"
            data-testid="contact-info"
          >
            <div className="absolute inset-0 tech-grid-bg-dark opacity-50" />
            <div className="relative">
              <div className="font-mono-tech text-[11px] tracking-[0.32em] uppercase text-[#FCD7C5] mb-6">
                Datos directos
              </div>

              <ul className="space-y-6">
                <ContactItem
                  icon={MapPin}
                  label="Dirección"
                  value="Esteban Alatorre 1288-A int. 3, San Juan Bosco C.P. 44730, Guadalajara, Jalisco, México."
                  testId="contact-address"
                />
                <ContactItem
                  icon={Phone}
                  label="Teléfono"
                  value="3321747329"
                  href="tel:3321747329"
                  testId="contact-phone"
                />
                <ContactItem
                  icon={Mail}
                  label="Email"
                  value="contacto@nexoraproductos.com"
                  href="mailto:contacto@nexoraproductos.com"
                  testId="contact-email"
                />
                <ContactItem
                  icon={Globe}
                  label="Página web"
                  value="nexoraproductos.com"
                  href="https://nexoraproductos.com"
                  testId="contact-web"
                />
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, name, type = "text", value, onChange, testId }) => (
  <label className="block">
    <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3B3838]/70">
      {label}
    </span>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      data-testid={testId}
      className="mt-2 w-full bg-[#f4f5f7] border border-[#3B3838]/20 px-4 py-3 text-[#3B3838] focus:outline-none focus:border-[#203864] focus:ring-1 focus:ring-[#203864] transition-all"
    />
  </label>
);

const ContactItem = ({ icon: Icon, label, value, href, testId }) => {
  const Tag = href ? "a" : "div";
  return (
    <li>
      <Tag
        href={href}
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel="noreferrer"
        data-testid={testId}
        className="flex items-start gap-4 group"
      >
        <div className="h-10 w-10 bg-[#843C0C] flex items-center justify-center flex-shrink-0 group-hover:bg-white group-hover:text-[#843C0C] text-white transition-colors">
          <Icon size={18} />
        </div>
        <div className="min-w-0">
          <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-white/60">
            {label}
          </div>
          <div className="text-sm md:text-base text-white group-hover:text-[#FCD7C5] transition-colors break-words">
            {value}
          </div>
        </div>
      </Tag>
    </li>
  );
};
